import { useAcademicYearAccess } from './useAcademicYearAccess';
import { isPastAcademicYear, useCurrentAcademicYear } from './useCurrentAcademicYear';

/** Whether the working year can be changed, and why not. */
export interface AcademicYearWritable {
  /**
   * False when the working year is a past one (closed, or ended before today), unless
   * the caller may edit closed years.
   */
  writable: boolean;
  /** Why it is view only, in words for a tooltip or caption; null when writable. */
  reason: string | null;
  /**
   * A note for a writable past year (an administrator editing it), else null. Shown by
   * the working-year notice (`YearScopeNotice` under `AcademicYearRequired`).
   */
  caption: string | null;
}

/** The view-only reason for a past working year. */
export const pastYearReason = (yearName: string): string => `${yearName} is a past academic year — view only.`;

/** The caption while an administrator edits a past working year. */
export const ADMIN_PAST_YEAR_CAPTION = 'Past academic year — editing as administrator';

/**
 * Whether pages may add or save in the working academic year. A past working year
 * (closed, or ended — chosen in the year switcher to look back) is view only, and the
 * server refuses writes to it, unless the caller may edit closed years
 * (`AcademicYearAccess.canEditClosedYear`); then it is writable with `caption`. While
 * those rights load, a past year stays view only. With no working year yet the page is
 * not rendered (see `AcademicYearRequired`), so this reports writable.
 */
export function useAcademicYearWritable(): AcademicYearWritable {
  const { currentYear } = useCurrentAcademicYear();
  const past = currentYear !== null && isPastAcademicYear(currentYear);
  // Only a past year needs the rights; an open year never asks.
  const { data: access } = useAcademicYearAccess({ enabled: past });
  if (currentYear && past) {
    if (access?.canEditClosedYear) {
      return { writable: true, reason: null, caption: ADMIN_PAST_YEAR_CAPTION };
    }
    return { writable: false, reason: pastYearReason(currentYear.yearName), caption: null };
  }
  return { writable: true, reason: null, caption: null };
}

/**
 * Whether a given year of the school can be written to, for the few actions that work
 * across years rather than in the working year. Promotion is one: it moves students
 * *out of* the working year (which may be past — the server treats the source side as
 * a system write) *into* a target year, and only the target must be open. An open year
 * is writable; a past one only for a closed-year administrator (view only while those
 * rights load). A year not among the school's years is left to the server.
 */
export function useYearWritable(): (yearId: number | '' | null) => boolean {
  const { years } = useCurrentAcademicYear();
  const anyPast = years.some((y) => isPastAcademicYear(y));
  const { data: access } = useAcademicYearAccess({ enabled: anyPast });
  const canEditClosed = access?.canEditClosedYear === true;
  return (yearId) => {
    const year = yearId === '' || yearId === null ? undefined : years.find((y) => y.yearId === yearId);
    return !year || !isPastAcademicYear(year) || canEditClosed;
  };
}

/**
 * Whether fees can be collected in the working year. Late fees are still collected
 * after a year has ended, so an ended year that is not Closed stays open for
 * collection for everyone; a Closed year is view only unless the caller may edit
 * closed years. Only fee collection uses this — other pages use
 * `useAcademicYearWritable`, which keeps ended years view only.
 */
export function useFeeCollectionWritable(): AcademicYearWritable {
  const { currentYear } = useCurrentAcademicYear();
  const closed = currentYear?.status === 'Closed';
  const { data: access } = useAcademicYearAccess({ enabled: closed });
  if (currentYear && closed) {
    if (access?.canEditClosedYear) {
      return { writable: true, reason: null, caption: ADMIN_PAST_YEAR_CAPTION };
    }
    return { writable: false, reason: `${currentYear.yearName} is closed — fees can no longer be collected.`, caption: null };
  }
  return { writable: true, reason: null, caption: null };
}
